import type { models } from "jiffy-api";
import type {
  PlacementData,
  PlacementInfo,
  PlacementInfoForm,
  DesignProjectResponse,
  ArtworkFile,
  DesignLabFormType,
  PlacementPayload
} from "@design-lab/shared/types";

/**
 * Build a PlacementPayload for submission from placement, info, and position.
 *
 * @param placement - PlacementData containing artworkId and size (width and height)
 * @param info - PlacementInfo specifying enhancement tier, optional note, and image-edit flags
 * @param position - Placement position identifier (for example `"front"` or `"front_left_chest"`)
 * @returns The assembled PlacementPayload with dimensions from `placement.size`, `position` set to `position`, enhancement fields and boolean flags populated from `info` (with defaults applied), `dtfArtworkId` from `placement.artworkId`, and `customNeckDistances` set to `null`.
 */
export function createPlacementPayload(
  placement: PlacementData,
  info: PlacementInfo,
  position: string
): PlacementPayload {
  return {
    width: placement.size.width,
    height: placement.size.height,
    position,
    enhanceImage: info.enhancementTier !== "print_original",
    enhancementRequestNote: info.enhancementRequestNote || "",
    enhancementTier: info.enhancementTier,
    removeBackground: info.removeBackground ?? false,
    editImageManual: info.editImageManual || false,
    dtfArtworkId: placement.artworkId,
    customNeckDistances: null
  };
}

// Helper function to get active front placement
export function getActiveFrontPlacement(artwork: DesignLabFormType["artwork"]) {
  const isLeftChest = artwork.selectedFrontPlacement === "frontLeftChest";
  const placement = isLeftChest ? artwork.frontLeftChest : artwork.front;
  const position = isLeftChest ? "front_left_chest" : "front";
  return { placement, position };
}

// Helper function to determine enhancement tier
export function getEnhancementTier(
  enhanceImage: boolean,
  enhancementTier?: string
): models.cart.madeAdd.EnhancementTier {
  if (!enhanceImage) return "print_original";
  return (enhancementTier || "full_enhancements") as models.cart.madeAdd.EnhancementTier;
}

/**
 * Converts a placement from a design project response into form-ready data for editing or creation.
 *
 * @param placement - A placement object from a design project response
 * @returns An object containing:
 *  - `placementData`: placement identifiers and numeric size (`artworkId`, `size.width`, `size.height`)
 *  - `info`: placement form info including `enhancementTier`, `removeBackground` (defaults to `false`), `enhancementRequestNote`, and `editImageManual` (defaults to `false`)
 *  - `artworkFile` (optional): present when the placement's artwork has a URL; contains `id` and `url`
 */
export function createPlacementFormData(placement: DesignProjectResponse["placements"][string]): {
  placementData: PlacementData;
  info: PlacementInfoForm;
  artworkFile?: ArtworkFile | undefined;
} {
  const { dtfArtwork } = placement;
  const url = dtfArtwork["url"] as string | undefined;

  return {
    placementData: {
      artworkId: String(placement.dtfArtwork.id),
      size: {
        width: Number(placement.width),
        height: Number(placement.height)
      }
    },
    info: {
      enhancementTier: getEnhancementTier(
        placement.enhanceImage,
        placement.dtfArtwork.enhancementTier
      ),
      removeBackground: placement.removeBackground ?? false,
      enhancementRequestNote: placement.enhancementRequestNote,
      editImageManual: placement.editImageManual || false
    },
    artworkFile: url
      ? {
          id: String(placement.dtfArtwork.id),
          url
        }
      : undefined
  };
}
